import { useQuery } from '@tanstack/react-query';
import axios from '../lib/axios';
import { useMemo, useState } from 'react';
import {
  CalendarSync,
  RefreshCw,
  MapPin,
  Download,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Ban,
  ArrowRight,
  ShieldAlert,
  Link2Off,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

/**
 * PPM Realignment — what the 45-day floor would do to every AMC contract that
 * still owes visits.
 *
 * A contract is sold as visits two months apart, created up front on a fixed
 * recurrence. When a visit is serviced late the ones after it stay put, so they
 * bunch up behind it and the tail of the contract empties out — or runs past
 * the expiry date, where a visit can never happen. This page re-spaces each
 * contract's pending visits from its last completed visit and shows the result
 * side by side with what is in Zuper today.
 *
 * Read-only: a preview, not an action. Nothing here moves or cancels a job.
 */

const ACTIONS = {
  keep: { label: 'Stays', dot: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  later: { label: 'Moves later', dot: 'bg-sky-500', text: 'text-sky-700', chip: 'bg-sky-50 text-sky-700 border-sky-200' },
  earlier: { label: 'Moves earlier', dot: 'bg-violet-500', text: 'text-violet-700', chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  cancel: { label: 'Past expiry — cancel', dot: 'bg-rose-500', text: 'text-rose-700', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  closing: { label: 'Closing visit', dot: 'bg-amber-500', text: 'text-amber-700', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  review: { label: 'Expiry already passed — review', dot: 'bg-orange-500', text: 'text-orange-700', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
  unknown: { label: 'No expiry on the buggy', dot: 'bg-slate-400', text: 'text-slate-600', chip: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const STATES = {
  aligned: { label: 'Already right', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  moved: { label: 'Re-spaced', chip: 'bg-sky-50 text-sky-700 border-sky-200' },
  compressed: { label: 'Compressed', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  trimmed: { label: 'Visits past expiry', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  final_window: { label: 'Final window', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  expiry_passed: { label: 'Expiry in the past', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
  no_expiry: { label: 'No expiry', chip: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'trimmed', label: 'Visits past expiry' },
  { key: 'final_window', label: 'Final window' },
  { key: 'compressed', label: 'Compressed' },
  { key: 'moved', label: 'Re-spaced' },
  { key: 'aligned', label: 'Already right' },
  { key: 'suspect', label: 'Expiry disputed' },
  { key: 'data', label: 'Expiry missing or past' },
];

const DAY_MS = 86400000;
const dayNum = (d) => Math.round(new Date(`${d}T00:00:00Z`).getTime() / DAY_MS);
const diffDays = (a, b) => dayNum(b) - dayNum(a);

function fmt(d) {
  if (!d) return '—';
  return new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
}
function fmtShort(d) {
  if (!d) return '';
  return new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

/**
 * One contract as a strip: what Zuper has on top, what the rule would make of
 * it underneath, the expiry as the wall on the right. Geometry is computed from
 * the dates so the spacing on screen is the spacing in days.
 */
const DOT = {
  keep: '#10b981', later: '#0ea5e9', earlier: '#8b5cf6',
  cancel: '#e11d48', closing: '#f59e0b', review: '#f97316', unknown: '#94a3b8',
};

function Strip({ contract, today }) {
  const W = 760;
  const PAD = 10;
  const Y_TOP = 10;        // the expiry / today captions
  const ROW_NOW = 38;
  const ROW_NEW = 76;

  const visits = contract.visits;
  const hasPlan = visits.some((v) => v.newDate);
  const H = hasPlan ? 98 : 56;
  const bottom = hasPlan ? ROW_NEW + 8 : ROW_NOW + 8;

  // An expiry that has long gone sits nowhere useful on the axis — it would
  // squash every visit into the right-hand edge. It is marked at the edge
  // instead and left out of the scale.
  const expiryInScale = contract.expiry && contract.expiry >= today ? contract.expiry : null;
  const dates = [
    today,
    ...visits.map((v) => v.currentDate),
    ...visits.map((v) => v.newDate).filter(Boolean),
    expiryInScale,
  ].filter(Boolean);

  const start = dates.reduce((a, b) => (a < b ? a : b));
  const end = dates.reduce((a, b) => (a > b ? a : b));
  const span = Math.max(diffDays(start, end), 1);
  const x = (d) => PAD + (diffDays(start, d) / span) * (W - PAD * 2);

  // Keep every caption inside the box: the ones at the edges hug them.
  const label = (px, y, text, fill, weight) => {
    const anchor = px < 26 ? 'start' : px > W - 26 ? 'end' : 'middle';
    const cx = px < 26 ? 2 : px > W - 26 ? W - 2 : px;
    return (
      <text x={cx} y={y} fontSize="8.5" fill={fill} textAnchor={anchor} fontWeight={weight}>
        {text}
      </text>
    );
  };

  const xToday = x(today);
  const xExpiry = expiryInScale ? x(expiryInScale) : null;
  // Where the contract ends and today are the same place, the expiry caption wins.
  const showToday = xExpiry === null || Math.abs(xExpiry - xToday) > 56;

  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[520px]" role="img"
        aria-label={`Visit timeline for ${contract.customerName}`}>
        <line x1={PAD} y1={ROW_NOW} x2={W - PAD} y2={ROW_NOW} stroke="#e2e8e5" strokeWidth="2" />
        {hasPlan && <line x1={PAD} y1={ROW_NEW} x2={W - PAD} y2={ROW_NEW} stroke="#e2e8e5" strokeWidth="2" />}

        {/* today */}
        <line x1={xToday} y1={Y_TOP + 6} x2={xToday} y2={bottom} stroke="#99a6a3" strokeWidth="1" />
        {showToday && label(xToday, Y_TOP, 'today', '#99a6a3')}

        {/* where the contract ends */}
        {xExpiry !== null && (
          <>
            <line x1={xExpiry} y1={Y_TOP + 6} x2={xExpiry} y2={bottom}
              stroke="#e11d48" strokeWidth="1.5" strokeDasharray="3 3" />
            {label(xExpiry, Y_TOP, `expiry ${fmtShort(contract.expiry)}`, '#e11d48', '600')}
          </>
        )}
        {contract.expiry && !expiryInScale && (
          <>
            <line x1={PAD - 4} y1={Y_TOP + 6} x2={PAD - 4} y2={bottom}
              stroke="#e11d48" strokeWidth="1.5" strokeDasharray="3 3" />
            {label(PAD, Y_TOP, `◀ expired ${fmtShort(contract.expiry)}`, '#e11d48', '600')}
          </>
        )}

        {visits.map((v, i) => {
          const xNow = x(v.currentDate);
          const a = ACTIONS[v.action] || ACTIONS.unknown;
          const colour = DOT[v.action] || DOT.unknown;
          const xNew = v.newDate ? x(v.newDate) : null;
          const room = visits.length <= 7;
          return (
            <g key={v.jobUid || i}>
              <title>{`${fmt(v.currentDate)} → ${v.newDate ? fmt(v.newDate) : 'cancel'} · ${a.label}`}</title>
              <circle cx={xNow} cy={ROW_NOW} r="4.5" fill="#64748b" />
              {room && label(xNow, ROW_NOW - 9, fmtShort(v.currentDate), '#64748b')}
              {xNew !== null ? (
                <>
                  <line x1={xNow} y1={ROW_NOW + 6} x2={xNew} y2={ROW_NEW - 6}
                    stroke={colour} strokeWidth="1.2" strokeOpacity="0.55" />
                  <circle cx={xNew} cy={ROW_NEW} r="5" fill={colour} />
                  {room && label(xNew, ROW_NEW + 15, fmtShort(v.newDate), colour, '600')}
                </>
              ) : v.action === 'cancel' ? (
                <g stroke="#e11d48" strokeWidth="1.8">
                  <line x1={xNow - 4} y1={ROW_NOW - 4} x2={xNow + 4} y2={ROW_NOW + 4} />
                  <line x1={xNow + 4} y1={ROW_NOW - 4} x2={xNow - 4} y2={ROW_NOW + 4} />
                </g>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** Visits done vs owed, as dots. */
function Progress({ done, pending, total }) {
  const n = Math.max(total, done + pending);
  const dots = [];
  for (let i = 0; i < Math.min(n, 14); i++) {
    dots.push(
      <span key={i}
        className={`inline-block w-2 h-2 rounded-full ${i < done ? 'bg-brand-primary' : 'bg-brand-light border border-brand-neutral/40'}`} />,
    );
  }
  return (
    <div className="flex items-center gap-1">
      {dots}
      <span className="ml-1 text-[11px] text-brand-neutral font-medium">{done} of {n} done</span>
    </div>
  );
}

function Tile({ value, label, sub, tone = 'default' }) {
  const tones = {
    default: 'border-brand-light text-brand-dark',
    rose: 'border-rose-300 text-rose-600',
    amber: 'border-amber-300 text-amber-600',
    sky: 'border-sky-300 text-sky-600',
    slate: 'border-slate-300 text-slate-600',
  };
  return (
    <div className={`border-2 rounded-xl px-4 py-3 bg-white ${tones[tone]}`}>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider mt-1.5">{label}</p>
      {sub && <p className="text-[11px] text-brand-neutral mt-0.5">{sub}</p>}
    </div>
  );
}

/** The whole fleet's pending visits as one bar, split by what happens to them. */
function OutcomeBar({ totals }) {
  const parts = [
    { key: 'keep', n: totals.kept },
    { key: 'later', n: totals.later },
    { key: 'earlier', n: totals.earlier },
    { key: 'closing', n: totals.closing },
    { key: 'cancel', n: totals.cancelled },
    { key: 'review', n: totals.review },
    { key: 'unknown', n: totals.unknown },
  ].filter((p) => p.n > 0);
  const sum = parts.reduce((a, p) => a + p.n, 0) || 1;

  return (
    <div>
      <div className="flex h-7 rounded-lg overflow-hidden border border-brand-light">
        {parts.map((p) => (
          <div key={p.key} className={`${ACTIONS[p.key].dot} flex items-center justify-center`}
            style={{ width: `${(p.n / sum) * 100}%` }} title={`${p.n} · ${ACTIONS[p.key].label}`}>
            {(p.n / sum) > 0.06 && <span className="text-[10px] font-bold text-white">{p.n}</span>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {parts.map((p) => (
          <span key={p.key} className="flex items-center gap-1.5 text-[11px] text-brand-neutral">
            <span className={`w-2.5 h-2.5 rounded-sm ${ACTIONS[p.key].dot}`} />
            {ACTIONS[p.key].label} <strong className="text-brand-dark">{p.n}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function downloadCSV(contracts, today) {
  const headers = ['Customer', 'Phone', 'Buggy', 'Serial', 'Location', 'WO#', 'Visit', 'Status',
    'AMC Expiry', 'Days Left', 'Recurrence Until', 'Expiry Disputed', 'Last Visit Done',
    'Current Date', 'New Date', 'Shift (days)', 'Outcome', 'Contract State', 'Visits Pending'];
  const rows = [headers.join(',')];
  const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  for (const c of contracts) {
    for (const v of c.visits) {
      rows.push([
        q(c.customerName), q(c.customerPhone), q(c.assetName), q(c.assetSerialNumber),
        q(c.workshopLocation), v.workOrderNumber || '', v.visitNo ?? '', q(v.statusName),
        c.expiry || '', c.daysLeft ?? '', c.untilDate || '', c.expirySuspect ? 'yes' : '',
        c.lastDoneDate || '', v.currentDate, v.newDate || '', v.shiftDays ?? '',
        q((ACTIONS[v.action] || {}).label), q((STATES[c.state] || {}).label), c.visitsPending,
      ].join(','));
    }
  }
  const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ppm-realignment-${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PPMRealignment() {
  const { selectedLocationId, currentLocation } = useLocationContext();
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState({});
  const zuperAppUrl = 'https://web.zuperpro.com';

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['ppm-realignment', selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/ppm-realignment');
      return data;
    },
  });

  const totals = data?.totals;
  const today = data?.today;
  const contracts = useMemo(() => data?.contracts || [], [data]);

  const counts = useMemo(() => {
    const c = { all: contracts.length, data: 0, suspect: 0 };
    for (const x of contracts) {
      c[x.state] = (c[x.state] || 0) + 1;
      if (x.state === 'expiry_passed' || x.state === 'no_expiry') c.data += 1;
      if (x.expirySuspect) c.suspect += 1;
    }
    return c;
  }, [contracts]);

  // Visits that would be cancelled on an expiry date the contract's own
  // recurrence disagrees with — the ones to check before anything is cancelled.
  const disputedVisits = useMemo(
    () => contracts.filter((c) => c.expirySuspect).reduce((a, c) => a + c.counts.cancel, 0),
    [contracts],
  );

  const shown = useMemo(() => {
    if (filter === 'all') return contracts;
    if (filter === 'suspect') return contracts.filter((c) => c.expirySuspect);
    if (filter === 'data') return contracts.filter((c) => c.state === 'expiry_passed' || c.state === 'no_expiry');
    return contracts.filter((c) => c.state === filter);
  }, [contracts, filter]);

  return (
    <Layout>
      <header className="bg-white border-b border-brand-light/60 p-3 md:p-4 flex justify-between items-center z-10 shadow-sm">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-brand-light to-white rounded-xl flex items-center justify-center shadow-sm">
            <CalendarSync className="w-5 h-5 text-brand-primary" />
          </div>
          <div>
            <h1 className="text-base md:text-xl font-bold text-brand-dark tracking-tight">PPM Realignment</h1>
            <p className="text-xs text-brand-neutral hidden md:block">
              Re-spacing the visits each AMC still owes — and dropping the ones that fall past expiry
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentLocation && (
            <span className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-brand-primary bg-brand-light/60 rounded-lg">
              <MapPin className="w-3 h-3" />
              {currentLocation.name}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => contracts.length > 0 && downloadCSV(contracts, today)}
            disabled={contracts.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-xl hover:bg-brand-secondary transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        {isLoading ? (
          <div className="bg-white rounded-2xl border border-brand-light/80 p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-primary" />
            <p className="text-sm text-brand-neutral mt-2">Reading every live AMC contract…</p>
          </div>
        ) : !totals ? (
          <div className="bg-white rounded-2xl border border-brand-light/80 p-12 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="text-sm text-brand-neutral">The realignment preview could not be built.</p>
          </div>
        ) : (
          <>
            {/* The rule, in one line each */}
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-4 mb-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-lg bg-brand-light/70 text-brand-dark text-[11px] font-semibold">
                  Spaced from the last completed visit
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-brand-light/70 text-brand-dark text-[11px] font-semibold">
                  {data.floorDays}-day floor
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-brand-light/70 text-brand-dark text-[11px] font-semibold">
                  ~{data.idealDays}-day target
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 text-[11px] font-semibold border border-rose-200">
                  Nothing past expiry
                </span>
              </div>
              <p className="text-xs text-brand-neutral leading-relaxed">
                A visit serviced late carries its delay to the visits after it, so they are laid out again from
                the day the last one was actually done: as close to two months apart as the contract allows, never
                closer than {data.floorDays} days, and never past the expiry on the buggy. Whatever no longer fits
                is listed for cancellation. In the last {data.floorDays} days of a contract the floor can place no
                visit at all, so there it governs only the gap <em>between</em> visits — one closing service stays
                and the contract is flagged as under-served. A visit already within {data.holdDays} days of its
                new slot is left where it is, and nothing is re-dated into the next {data.leadDays} days: the
                customer has to be told and the workshop day has to be free.
              </p>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Tile value={totals.contracts} label="AMCs owing visits" sub={`at ${data.location || 'all locations'}`} />
              <Tile value={totals.pendingVisits} label="Visits pending" sub={`${totals.kept} already right`} />
              <Tile value={totals.moved} label="Visits re-dated" tone="sky"
                sub={`${totals.later} later · ${totals.earlier} earlier`} />
              <Tile value={totals.cancelled} label="Past expiry → cancel" tone="rose"
                sub={`across ${counts.trimmed || 0} contracts`} />
              <Tile value={totals.finalWindow} label="In the final window" tone="amber"
                sub="one closing visit, rest flagged" />
            </div>

            {/* What happens to all pending visits */}
            <div className="bg-white rounded-2xl shadow-sm border border-brand-light/80 p-4 mb-4">
              <h2 className="text-sm font-bold text-brand-dark mb-3">
                All {totals.pendingVisits} pending visits, by outcome
              </h2>
              <OutcomeBar totals={totals} />
            </div>

            {/* What the plan cannot be trusted on */}
            {(totals.expiryPassed > 0 || totals.noExpiry > 0 || totals.expirySuspect > 0
              || data.coverage?.unlinkedUpcoming > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
                <div className="flex items-start gap-2.5">
                  <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900 leading-relaxed space-y-1.5">
                    <p className="font-bold text-sm text-amber-800">Before anything is cancelled</p>
                    {totals.expirySuspect > 0 && (
                      <p>
                        <strong>{totals.expirySuspect} contracts</strong> would lose{' '}
                        <strong>{disputedVisits} visits</strong> to an <em>AMC Expiry</em> that their own Zuper
                        recurrence disagrees with by more than two months — the series runs on well past the date
                        the buggy says the contract ends. One of the two is wrong, and it has to be settled before
                        those visits go.
                      </p>
                    )}
                    {totals.expiryPassed > 0 && (
                      <p>
                        <strong>{totals.expiryPassed} contracts</strong> have an <em>AMC Expiry</em> that has
                        already passed while visits are still being serviced — {totals.review} visits between
                        them. Those are listed for review, never cancelled: a wrong expiry date would throw away
                        visits a customer has paid for.
                      </p>
                    )}
                    {totals.noExpiry > 0 && (
                      <p>
                        <strong>{totals.noExpiry} contracts</strong> have no <em>AMC Expiry</em> on the buggy at
                        all, so there is nothing to plan against — {totals.unknown} visits left as they are.
                      </p>
                    )}
                    {data.coverage?.unlinkedUpcoming > 0 && (
                      <p className="flex items-start gap-1.5">
                        <Link2Off className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                          <strong>{data.coverage.unlinkedUpcoming} upcoming visits</strong> are not linked to
                          their series in the mirror ({data.coverage.linked} of {data.coverage.recurringJobs}{' '}
                          linked), so this page cannot see which contract they belong to.
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                    filter === f.key
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-brand-dark border-brand-light hover:bg-brand-light/40'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 ${filter === f.key ? 'text-white/80' : 'text-brand-neutral'}`}>
                    {counts[f.key] || 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Contracts */}
            {shown.length === 0 ? (
              <div className="bg-white rounded-2xl border border-brand-light/80 p-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-lg font-bold text-brand-dark">Nothing here</p>
                <p className="text-sm text-brand-neutral">No contract matches this filter.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shown.map((c) => {
                  const st = STATES[c.state] || STATES.no_expiry;
                  const isOpen = !!open[c.recurringJobId];
                  const tight = c.daysLeft !== null && c.daysLeft < data.floorDays;
                  return (
                    <div key={c.recurringJobId}
                      className="bg-white rounded-2xl shadow-sm border border-brand-light/80 overflow-hidden">
                      <div className="p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-brand-dark">{c.customerName}</span>
                              <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${st.chip}`}>
                                {st.label}
                              </span>
                              {c.underServed && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                                  Under-served
                                </span>
                              )}
                              {c.expirySuspect && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-300"
                                  title={`The recurrence runs to ${fmt(c.untilDate)} — ${c.untilGapDays} days past this expiry`}>
                                  Expiry disputed
                                </span>
                              )}
                              {c.visitsOverdue > 0 && (
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-300">
                                  {c.visitsOverdue} overdue
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-brand-neutral mt-0.5">
                              {c.assetName || 'Buggy unknown'}
                              {c.assetSerialNumber && <span className="font-mono"> · {c.assetSerialNumber}</span>}
                              {c.workshopLocation && <span> · {c.workshopLocation}</span>}
                            </p>
                            <div className="mt-2"><Progress done={c.visitsDone} pending={c.visitsPending} total={c.visitsTotal} /></div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider">Pending</p>
                              <p className="text-2xl font-bold text-brand-dark leading-none">{c.visitsPending}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider">AMC expiry</p>
                              <p className={`text-sm font-bold ${tight ? 'text-rose-600' : 'text-brand-dark'}`}>{fmt(c.expiry)}</p>
                              <p className={`text-[11px] ${tight ? 'text-rose-500' : 'text-brand-neutral'}`}>
                                {c.daysLeft === null ? 'not set'
                                  : c.daysLeft < 0 ? `${Math.abs(c.daysLeft)} days ago`
                                    : `${c.daysLeft} days left`}
                              </p>
                            </div>
                            {c.gapDays && (
                              <div className="text-right">
                                <p className="text-[10px] font-semibold text-brand-neutral uppercase tracking-wider">New gap</p>
                                <p className="text-sm font-bold text-brand-dark">{c.gapDays} d</p>
                              </div>
                            )}
                            <button
                              onClick={() => setOpen((o) => ({ ...o, [c.recurringJobId]: !isOpen }))}
                              className="p-2 rounded-xl hover:bg-brand-light/60 transition-colors"
                              aria-label={isOpen ? 'Hide visits' : 'Show visits'}
                            >
                              {isOpen ? <ChevronDown className="w-5 h-5 text-brand-dark" />
                                : <ChevronRight className="w-5 h-5 text-brand-dark" />}
                            </button>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-brand-light/60">
                          <div className="flex items-center gap-3 mb-1 text-[10px] font-semibold text-brand-neutral uppercase tracking-wider">
                            <span className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-full bg-slate-500" /> in Zuper now
                            </span>
                            {c.visits.some((v) => v.newDate) && (
                              <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-brand-primary" /> realigned
                              </span>
                            )}
                          </div>
                          <Strip contract={c} today={today} />
                        </div>
                      </div>

                      {isOpen && (
                        <div className="border-t border-brand-light/60 bg-brand-light/10">
                          <table className="w-full text-left text-xs">
                            <thead className="text-brand-neutral font-semibold border-b border-brand-light/60">
                              <tr>
                                <th className="px-4 py-2">WO#</th>
                                <th className="px-4 py-2">Visit</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">In Zuper</th>
                                <th className="px-4 py-2">Realigned to</th>
                                <th className="px-4 py-2">Shift</th>
                                <th className="px-4 py-2">Outcome</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-brand-light/50">
                              {c.visits.map((v) => {
                                const a = ACTIONS[v.action] || ACTIONS.unknown;
                                return (
                                  <tr key={v.jobUid} className="hover:bg-white/60">
                                    <td className="px-4 py-2 font-mono text-brand-neutral">
                                      <span className="inline-flex items-center gap-1">
                                        {v.workOrderNumber || '—'}
                                        {v.jobUid && (
                                          <button
                                            onClick={() => window.open(`${zuperAppUrl}/jobs/${v.jobUid}/details`, '_blank')}
                                            className="p-0.5 rounded hover:bg-brand-light hover:text-brand-primary opacity-50 hover:opacity-100"
                                            aria-label="Open in Zuper"
                                          >
                                            <ExternalLink className="w-3 h-3" />
                                          </button>
                                        )}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-brand-dark">{v.jobTitle || `Visit ${v.visitNo}`}</td>
                                    <td className="px-4 py-2 text-brand-neutral">{v.statusName || '—'}</td>
                                    <td className="px-4 py-2 text-brand-dark">{fmt(v.currentDate)}</td>
                                    <td className="px-4 py-2">
                                      {v.newDate ? (
                                        <span className={`inline-flex items-center gap-1 font-semibold ${a.text}`}>
                                          <ArrowRight className="w-3 h-3" />{fmt(v.newDate)}
                                        </span>
                                      ) : v.action === 'cancel' ? (
                                        <span className="inline-flex items-center gap-1 font-semibold text-rose-600">
                                          <Ban className="w-3 h-3" />cancel
                                        </span>
                                      ) : <span className="text-brand-neutral">—</span>}
                                    </td>
                                    <td className="px-4 py-2 text-brand-neutral">
                                      {v.shiftDays === null || v.shiftDays === 0 ? '—'
                                        : `${v.shiftDays > 0 ? '+' : ''}${v.shiftDays} d`}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${a.chip}`}>
                                        {a.label}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          <p className="px-4 py-2.5 text-[11px] text-brand-neutral">
                            {c.lastDoneDate ? <>Last visit completed {fmt(c.lastDoneDate)}</> : <>No visit completed yet</>}
                            {' · '}laid out from {fmt(c.anchor)}
                            {c.roomDays !== null && <> · {c.roomDays} days of contract left to fill</>}
                            {c.untilDate && (
                              <span className={c.expirySuspect ? 'text-orange-700 font-semibold' : undefined}>
                                {' · '}recurrence runs to {fmt(c.untilDate)}
                                {c.untilGapDays > 0 && <> ({c.untilGapDays} d past expiry)</>}
                              </span>
                            )}
                            {c.rrule && <span className="font-mono"> · {c.rrule.split(';').slice(0, 2).join(';')}</span>}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-[11px] text-brand-neutral text-center mt-5">
              Preview only — nothing on this page moves or cancels a job in Zuper. Read {fmt(today)} from the
              live mirror, categories {data.categories.join(', ')}.
            </p>
          </>
        )}
      </main>
    </Layout>
  );
}
